import { useMemo } from "react";
import { useAllFranchisees } from "../useAllFranchisees.ts";
import { TeacherScoreWithId, useTeacherScores } from "./useTeacherScores.ts";

export type TeacherRanking = {
  position: number;
  teacherCount: number;
  topPercentage: number;
  finalScore: number;
  otherTeachersFinalScores: number[];
};

function rankTeacherByFinalScore(
  teacherId: string,
  allScores: TeacherScoreWithId[],
): TeacherRanking | null {
  const teacherScore = allScores.find((score) => score.teacherId === teacherId);
  if (!teacherScore) return null;

  const teachersAhead = allScores.filter(
    (score) => score.finalScore > teacherScore.finalScore,
  ).length;
  const position = teachersAhead + 1;

  return {
    position,
    teacherCount: allScores.length,
    topPercentage: Math.ceil((position / allScores.length) * 100),
    finalScore: teacherScore.finalScore,
    otherTeachersFinalScores: allScores
      .filter((score) => score.teacherId !== teacherId)
      .map((score) => score.finalScore),
  };
}

export function useTeacherRanking(teacherId: string) {
  const { franchisees, loading: loadingTeachers } = useAllFranchisees();
  const teacherIds = useMemo(
    () => franchisees.map((franchisee) => franchisee.id),
    [franchisees],
  );
  const { scoreByTeacherId, loading: loadingScores } =
    useTeacherScores(teacherIds);

  const ranking = useMemo(
    () =>
      rankTeacherByFinalScore(teacherId, Array.from(scoreByTeacherId.values())),
    [teacherId, scoreByTeacherId],
  );

  return {
    ranking,
    loading: (loadingTeachers || loadingScores) && !ranking,
  };
}
